import { ArrowLeft, ShoppingCart, Search } from "lucide-react";
import type {
  PageLayout,
  Section,
  CategorySectionData,
  SuggestedProductsSectionData,
} from "../../../../src/types";

const MobilePreview: React.FC<{ layoutData: PageLayout | null }> = ({
  layoutData,
}) => {
  const getGridCols = (length: number): string => {
    switch (length) {
      case 1:
        return "grid-cols-1";
      case 2:
        return "grid-cols-2";
      case 3:
        return "grid-cols-3";
      default:
        return "grid-cols-4";
    }
  };

  const renderSection = (section: Section) => {
    // Get padding and background color from section.data, with defaults
    let padding = "p-4";
    let backgroundColor = "#fff";
    if (
      section.data &&
      typeof section.data === "object" &&
      !Array.isArray(section.data)
    ) {
      padding = (section.data as any).padding || "p-4";
      backgroundColor = (section.data as any).backgroundColor || "#fff";
    }
    const style = { backgroundColor };
    switch (section.type) {
      case "banner":
        return (
          <div className={`w-full relative ${padding}`} style={style}>
            {section.data.image ? (
              <img
                src={section.data.image}
                alt="Banner"
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                <span className="text-gray-400">Banner Image</span>
              </div>
            )}
          </div>
        );
      case "carousel":
        return (
          <div className={`w-full overflow-hidden ${padding}`} style={style}>
            <div className="relative">
              <div className="flex space-x-2 overflow-x-auto py-2 scrollbar-hide snap-x snap-mandatory">
                {section.data.map((item, index) => (
                  <div
                    key={`carousel-${index}-${
                      item.image?.slice(0, 10) || "no-image"
                    }`}
                    className="flex-none w-full snap-center"
                  >
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={`Carousel ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-full h-32 bg-gray-200 rounded-lg flex items-center justify-center">
                        <span className="text-gray-400 text-xs">
                          Carousel Image {index + 1}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {/* Carousel dots */}
              <div className="flex justify-center mt-2 gap-1">
                {section.data.map((_, index) => (
                  <div
                    key={`dot-${index}-${section.data.length}`}
                    className={`w-1.5 h-1.5 rounded-full ${
                      index === 0 ? "bg-blue-500" : "bg-gray-300"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        );
      case "card":
        return (
          <div className={`w-full overflow-hidden ${padding}`} style={style}>
            <div className={`grid ${getGridCols(section.data.length)} gap-2`}>
              {section.data.map((item, index) => (
                <div
                  key={`card-${index}-${item.title}`}
                  className="bg-white p-3 rounded-lg shadow-sm border border-gray-100"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      {item.icon ? (
                        <img
                          src={item.icon}
                          alt={item.title}
                          className="w-10 h-10 object-contain rounded-md"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gray-200 rounded-md flex items-center justify-center">
                          <span className="text-gray-400 text-xs">Icon</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-sm text-gray-900 truncate">
                        {item.title || "Title"}
                      </h3>
                      {item.subtitle && (
                        <p className="text-xs text-green-600 font-medium">
                          {item.subtitle}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case "categories": {
        const categoriesData = section.data as CategorySectionData;
        return (
          <div className={`w-full ${padding}`} style={style}>
            {categoriesData.title && (
              <h4 className="text-sm font-semibold text-gray-900 mb-2">
                {categoriesData.title}
              </h4>
            )}
            {categoriesData.caption && (
              <p className="text-xs text-gray-600 mb-3">
                {categoriesData.caption}
              </p>
            )}
            <div
              className={`grid ${getGridCols(categoriesData.columns)} gap-3`}
            >
              {categoriesData.items.map((item, index) => (
                <div
                  key={`category-${index}-${item.title}`}
                  className="flex flex-col items-center"
                >
                  <div className="w-14 h-14 mb-1">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-contain rounded-md"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 rounded-md flex items-center justify-center">
                        <span className="text-gray-400 text-xs">IMG</span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-700 text-center leading-tight">
                    {item.title || "Category"}
                  </p>
                </div>
              ))}
            </div>
          </div>
        );
      }
      case "suggested_products": {
        const suggestedProductsData =
          section.data as SuggestedProductsSectionData;
        return (
          <div className={`w-full ${padding}`} style={style}>
            {suggestedProductsData.title && (
              <h4 className="text-sm font-semibold text-gray-900 mb-1">
                {suggestedProductsData.title}
              </h4>
            )}
            {suggestedProductsData.subtitle && (
              <p className="text-xs text-gray-600 mb-3">
                {suggestedProductsData.subtitle}
              </p>
            )}
            <div className="overflow-x-auto">
              <div className="flex space-x-3 pb-2">
                {/* Mock product cards - in real implementation, these would be loaded from the API */}
                {Array.from({ length: 5 }, (_, i) => (
                  <div
                    key={`product-card-${i + 1}`}
                    className="flex-none w-32 bg-white rounded-lg shadow-sm border border-gray-100 p-3"
                  >
                    <div className="w-full h-20 bg-gray-200 rounded-md mb-2 flex items-center justify-center">
                      <span className="text-gray-400 text-xs">
                        Product {i + 1}
                      </span>
                    </div>
                    <h5 className="text-xs font-medium text-gray-900 truncate mb-1">
                      Product Name
                    </h5>
                    <p className="text-xs text-green-600 font-medium">₹199</p>
                    <button
                      type="button"
                      className="w-full mt-2 bg-blue-500 text-white text-xs py-1 rounded-md"
                    >
                      Add to Cart
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="w-full max-w-md mx-auto p-6">
      {/* Mobile Device Frame */}
      <div className="relative mx-auto w-[375px] h-[667px] bg-black rounded-[2.5rem] shadow-2xl p-2">
        <div className="w-full h-full bg-white rounded-[2rem] overflow-hidden">
          {/* Status Bar */}
          <div className="bg-white px-6 py-2 flex justify-between items-center text-xs">
            <span className="font-medium">9:41</span>
          </div>

          {/* App Header */}
          <div className="bg-transparent px-4 py-3 flex items-center justify-between">
            <h1 className="text-black font-medium flex-1 text-center">
              {layoutData?.page || "Page Preview"}
            </h1>
            <div className="flex gap-3">
              <Search className="w-5 h-5 text-black" />
              <ShoppingCart className="w-5 h-5 text-black" />
            </div>
          </div>

          {/* Screen Content */}
          <div className="h-[calc(100%-88px)] overflow-y-auto bg-gray-50">
            {/* Layout Sections */}
            <div className="space-y-4 pb-4">
              {layoutData?.metadata?.map((section: Section, index: number) => (
                <div
                  key={`${section.type}-${index}`}
                  className="bg-white px-4 py-3"
                >
                  {renderSection(section)}
                </div>
              ))}

              {(!layoutData?.metadata || layoutData.metadata.length === 0) && (
                <div className="flex items-center justify-center h-64 text-gray-400">
                  <div className="text-center">
                    <p className="text-sm">No sections added</p>
                    <p className="text-xs mt-1">Add sections to see preview</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobilePreview;
